import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useLocale } from '@/hooks';
import { BoltFillIcon, MapFillIcon, MapIcon, MessageLinesFillIcon, PersonPlusFillIcon, SparklingFillIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { Button } from '@/ui_v3';
import { cn } from '@/utils';
import { handleContactSalesClick } from '../utils';
import { ZepQuizBizLogo } from './ZepQuizBizLogo';
export const BizPlan = () => {
  return <section className="flex flex-col items-center gap-2xl px-xl py-3xl" data-sentry-component="BizPlan" data-sentry-source-file="BizPlan.tsx">
      <Title data-sentry-element="Title" data-sentry-source-file="BizPlan.tsx" />
      <PricingCards data-sentry-element="PricingCards" data-sentry-source-file="BizPlan.tsx" />
    </section>;
};
const Title = () => {
  const {
    t
  } = useTranslation(['bizLanding']);
  const {
    locale
  } = useLocale();
  return <div className={cn('flex max-w-[1140px] flex-wrap items-center justify-center gap-2xs', locale === 'ko' || locale === 'ja' ? 'flex-row' : 'flex-row-reverse gap-sm')} data-sentry-component="Title" data-sentry-source-file="BizPlan.tsx">
      <ZepQuizBizLogo className="relative bottom-[4px]" data-sentry-element="ZepQuizBizLogo" data-sentry-source-file="BizPlan.tsx" />
      <p className="text-display-sm-extrabold text-default">
        {t('biz-landing.plan.title')}
      </p>
    </div>;
};
const PricingCards = () => {
  return <div className="flex w-full flex-col items-center justify-center gap-lg min-[751px]:flex-row min-[751px]:items-stretch" data-sentry-component="PricingCards" data-sentry-source-file="BizPlan.tsx">
      <FreePlanCard data-sentry-element="FreePlanCard" data-sentry-source-file="BizPlan.tsx" />
      <EnterprisePlanCard data-sentry-element="EnterprisePlanCard" data-sentry-source-file="BizPlan.tsx" />
    </div>;
};
const FreePlanCard = () => {
  const {
    t
  } = useTranslation(['bizLanding']);
  const router = useRouter();
  const {
    isAuthenticated
  } = useUser();
  const handleClick = () => {
    if (isAuthenticated) {
      void router.push('/public');
    } else {
      void router.push('/login');
    }
  };
  return <div className="flex w-full min-w-[300px] max-w-[400px] flex-col gap-lg overflow-hidden rounded-[24px] border border-gray-200 bg-default shadow-sm" data-sentry-component="FreePlanCard" data-sentry-source-file="BizPlan.tsx">
      {/* Card Top */}
      <div className="flex h-[200px] flex-col items-center bg-gradient-to-b from-gray-50 to-gray-100 px-xl pb-lg pt-xl">
        <div className="flex w-full flex-col gap-md">
          <div className="flex flex-col gap-3xs">
            <p className="text-heading-lg-extrabold text-neutral opacity-90">
              FREE
            </p>
            <p className="text-body-lg-bold text-neutral">
              {t('biz-landing.plan.free.description')}
            </p>
          </div>
          <p className="text-heading-lg-extrabold text-gray-800">
            {t('biz-landing.plan.free.price')}
          </p>
        </div>
      </div>

      {/* Functions */}
      <div className="flex flex-1 flex-col gap-lg px-xl">
        <p className="text-body-md-bold text-default">
          {t('biz-landing.plan.free.featuresTitle')}
        </p>
        <div className="flex flex-col gap-sm">
          <FeatureItem icon={<MapIcon size="sm" color={ICON_COLOR.alternative} />} text={t('biz-landing.plan.free.feature1')} data-sentry-element="FeatureItem" data-sentry-source-file="BizPlan.tsx" />
          <FeatureItem icon={<SparklingFillIcon size="sm" color={ICON_COLOR.assistive} />} text={t('biz-landing.plan.free.feature2')} data-sentry-element="FeatureItem" data-sentry-source-file="BizPlan.tsx" />
        </div>
      </div>

      {/* Button */}
      <div className="px-xl pb-xl pt-xs">
        <Button variant="contained" intent="neutral" size="lg" className="w-full" onClick={handleClick} data-sentry-element="Button" data-sentry-source-file="BizPlan.tsx">
          {t('biz-landing.plan.free.button')}
        </Button>
      </div>
    </div>;
};
const EnterprisePlanCard = () => {
  const {
    t
  } = useTranslation(['bizLanding']);
  const {
    locale
  } = useLocale();
  return <div className="w-full min-w-[300px] max-w-[400px] rounded-[24px] bg-gradient-to-r from-violet-300 to-mint-600 p-[2px] shadow-sm" data-sentry-component="EnterprisePlanCard" data-sentry-source-file="BizPlan.tsx">
      <div className="flex h-full flex-col gap-lg overflow-hidden rounded-[22px] bg-default">
        {/* Card Top */}
        <div className="flex h-[200px] flex-col items-start overflow-hidden bg-gradient-to-r from-violet-300 to-mint-600 px-xl pb-lg pt-xl">
          <div className="flex w-full flex-col gap-md">
            <div className="flex flex-col gap-3xs">
              <p className="text-heading-lg-extrabold text-inverse opacity-90">
                ENTERPRISE
              </p>
              <p className="text-body-lg-bold text-inverse">
                {t('biz-landing.plan.enterprise.description')}
              </p>
            </div>
            <p className="text-heading-lg-extrabold text-white">
              {t('biz-landing.plan.enterprise.price')}
            </p>
          </div>
        </div>

        {/* Functions */}
        <div className="flex flex-col gap-lg px-xl">
          <p className="text-body-md-bold text-strong">
            {t('biz-landing.plan.enterprise.featuresTitle')}
          </p>
          <div className="flex flex-col gap-sm">
            <FeatureItem icon={<MapFillIcon size="sm" color={ICON_COLOR.alternative} />} text={t('biz-landing.plan.enterprise.feature1')} isEnterprise data-sentry-element="FeatureItem" data-sentry-source-file="BizPlan.tsx" />
            <FeatureItem icon={<MessageLinesFillIcon size="sm" color={ICON_COLOR.alternative} />} text={t('biz-landing.plan.enterprise.feature2')} isEnterprise data-sentry-element="FeatureItem" data-sentry-source-file="BizPlan.tsx" />
            <FeatureItem icon={<PersonPlusFillIcon size="sm" color={ICON_COLOR.alternative} />} text={t('biz-landing.plan.enterprise.feature3')} isEnterprise data-sentry-element="FeatureItem" data-sentry-source-file="BizPlan.tsx" />
            <FeatureItem icon={<SparklingFillIcon size="sm" color={ICON_COLOR.alternative} />} text={t('biz-landing.plan.enterprise.feature4')} isEnterprise isBold data-sentry-element="FeatureItem" data-sentry-source-file="BizPlan.tsx" />
          </div>
        </div>

        {/* Button */}
        <div className="px-xl pb-xl pt-xs">
          <Button variant="contained" intent="gradient" size="lg" className="w-full" leftSlot={<BoltFillIcon size="md" />} onClick={() => handleContactSalesClick(locale)} data-sentry-element="Button" data-sentry-source-file="BizPlan.tsx">
            {t('biz-landing.plan.enterprise.button')}
          </Button>
        </div>
      </div>
    </div>;
};
const FeatureItem = ({
  icon,
  text,
  isEnterprise = false,
  isBold = false
}: {
  icon: React.ReactNode;
  text: string;
  isEnterprise?: boolean;
  isBold?: boolean;
}) => {
  return <div className="flex h-[24px] items-center gap-[6px]" data-sentry-component="FeatureItem" data-sentry-source-file="BizPlan.tsx">
      {icon}
      <p className={isEnterprise ? isBold ? 'text-body-lg-bold text-default' : 'text-body-lg-medium text-default' : 'text-body-lg-bold text-neutral'}>
        {text}
      </p>
    </div>;
};